import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { PriceHistory } from '../types';

// Register standard Chart.js components
Chart.register(...registerables);

interface PriceChartProps {
  priceHistory?: PriceHistory[];
}

const PriceChart: React.FC<PriceChartProps> = ({ priceHistory = [] }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current || !priceHistory || priceHistory.length === 0) return;

    // Clean up existing instance
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Define modern colors matching the splash screen theme
    const theme = {
      bg: '#0B0F19',
      accent: '#10b981', // Emerald
      accentMuted: 'rgba(16, 185, 129, 0.15)',
      red: '#ef4444',
      redMuted: 'rgba(239, 68, 68, 0.15)',
      grid: 'rgba(255, 255, 255, 0.03)',
      text: '#9ca3af',
      border: 'rgba(255, 255, 255, 0.05)'
    };

    // Create a beautiful vertical gradient for the area chart
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, 'rgba(16, 185, 129, 0.25)');
    gradient.addColorStop(0.5, 'rgba(16, 185, 129, 0.05)');
    gradient.addColorStop(1, 'transparent');

    // Transform data for Area Chart (Close prices)
    const labels = priceHistory.map(h => new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    const prices = priceHistory.map(h => h.close || h.price || 0);
    
    // Volume data
    const volumes = priceHistory.map(h => h.volume || 0);

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Price',
            data: prices,
            borderColor: theme.accent,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: theme.accent,
            pointHoverBorderColor: '#fff',
            pointHoverBorderWidth: 2,
            fill: true,
            backgroundColor: gradient,
            tension: 0.4, // Smooth curve
          },
          {
            label: 'Volume',
            type: 'bar',
            data: volumes,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            yAxisID: 'volume',
            barThickness: 'flex',
            maxBarThickness: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 0,
            right: 10,
            top: 40,
            bottom: 10
          }
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(10, 15, 25, 0.95)',
            titleFont: { size: 11, weight: 'bold' },
            bodyFont: { size: 12 },
            padding: 12,
            cornerRadius: 8,
            borderColor: theme.border,
            borderWidth: 1,
            displayColors: false,
            callbacks: {
              label: (context) => {
                if (context.datasetIndex === 1) return `VOL: ${context.parsed.y.toLocaleString()}`;
                return `Price: ₹${context.parsed.y.toFixed(2)}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: theme.text,
              font: { size: 10 },
              maxRotation: 0,
              autoSkip: true,
            }
          },
          y: {
            position: 'right',
            grid: {
              color: theme.grid,
              drawTicks: false
            },
            border: { display: false },
            ticks: {
              color: theme.text,
              font: { size: 10 },
              callback: (val) => '₹' + val.toLocaleString()
            }
          },
          volume: {
            type: 'linear',
            display: false,
            position: 'left',
            max: Math.max(...volumes) * 5, 
          }
        }
      },
    });

    return () => {
      if (chartInstance.current) chartInstance.current.destroy();
    };
  }, [priceHistory]);

  const latestPrice = priceHistory[priceHistory.length - 1];
  const previousPrice = priceHistory[priceHistory.length - 2];
  const change = latestPrice && previousPrice ? (latestPrice.close || 0) - (previousPrice.close || 0) : 0;
  const changePercent = previousPrice ? (change / (previousPrice.close || 1)) * 100 : 0;

  return (
    <div className="w-full h-full rounded-xl border border-white/5 p-4 shadow-2xl relative overflow-hidden">
      {/* Dynamic Header */}
      <div className="absolute top-6 left-6 z-10 pointer-events-none">
        <div className="flex flex-col">
          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-[0.2em]">Market Pulse</span>
          <div className="flex items-baseline gap-3 mt-1">
             <span className="text-2xl font-black text-white tracking-tight">
                ₹{latestPrice?.close?.toFixed(2) || "0.00"}
             </span>
             <div className={`flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md ${
               change >= 0 ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
             }`}>
               <span>{change >= 0 ? '▲' : '▼'}</span>
               <span>{Math.abs(changePercent).toFixed(2)}%</span>
             </div>
          </div>
        </div>
      </div>

      {/* Main Canvas */}
      {!priceHistory || priceHistory.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-700">
           <div className="w-12 h-12 rounded-full border-2 border-dashed border-gray-800 animate-spin mb-4" />
           <p className="text-[10px] uppercase tracking-widest font-bold">Awaiting Market Feed</p>
        </div>
      ) : (
        <canvas ref={chartRef} className="w-full h-full" />
      )}

      {/* Decorative Scanline Effect */}
      <div className="absolute inset-0 pointer-events-none border-1"></div>
    </div>
  );
};

export default PriceChart;